import type { UnknownAction } from '@reduxjs/toolkit';

import type { MakeRequired } from '@polyai/stream/types/make-required';

import { deleteEntity } from './reducers/entities/actions';
import {
  createFlowStep,
  createNoCodeStep,
  deleteFlowStep,
  updateFlowStep,
  updateNoCodeStep,
} from './reducers/flows/actions';
import { deleteFunction } from './reducers/functions/actions';
import { deleteHandoff } from './reducers/handoff/actions';
import { createTopic, updateTopic } from './reducers/knowledge-base/actions';
import { deleteTemplate } from './reducers/sms/actions';
import {
  createStopKeywords,
  updateStopKeywords,
} from './reducers/stop-keywords/actions';
import { deleteAttribute } from './reducers/variant/actions';

type CreateTopic = ReturnType<typeof createTopic>;

type UpdateTopic = ReturnType<typeof updateTopic>;

type CreateFlowStep = ReturnType<typeof createFlowStep>;

type UpdateFlowStep = ReturnType<typeof updateFlowStep>;

type DeleteTemplate = ReturnType<typeof deleteTemplate>;

type DeleteHandoff = ReturnType<typeof deleteHandoff>;

type DeleteFunction = ReturnType<typeof deleteFunction>;

type DeleteFlowStep = ReturnType<typeof deleteFlowStep>;

type DeleteAttributePayload = ReturnType<typeof deleteAttribute>;

type DeleteEntity = ReturnType<typeof deleteEntity>;

type CreateStopKeywords = ReturnType<typeof createStopKeywords>;

type UpdateStopKeywords = ReturnType<typeof updateStopKeywords>;

type CreateNoCodeStep = ReturnType<typeof createNoCodeStep>;

type UpdateNoCodeStep = ReturnType<typeof updateNoCodeStep>;

// match an action that changes which SMS templates a KB topic references
export const isUpdateTopicReferencesSms = (
  action: UnknownAction,
): action is MakeRequired<UpdateTopic, 'payload.references.sms'> => {
  if (action.type === updateTopic.type) {
    const { payload } = action as UpdateTopic;
    return payload.references?.sms !== undefined;
  }

  return false;
};

// match an action that updates which handoffs a KB topic references
export const isUpdateTopicReferencesHandoff = (
  action: UnknownAction,
): action is MakeRequired<UpdateTopic, 'payload.references.handoff'> => {
  if (action.type === updateTopic.type) {
    const { payload } = action as UpdateTopic;
    return payload.references?.handoff !== undefined;
  }
  return false;
};

// match an action that updates which attributes a KB topic references
export const isUpdateTopicReferencesAttribute = (
  action: UnknownAction,
): action is MakeRequired<UpdateTopic, 'payload.references.attribute'> => {
  if (action.type === updateTopic.type) {
    const { payload } = action as UpdateTopic;
    return payload.references?.attributes !== undefined;
  }
  return false;
};

// Flow Steps

// match an action that updates which SMS templates a flow step references
export const isStepReferencesSms = (
  action: UnknownAction,
): action is MakeRequired<
  CreateFlowStep | UpdateFlowStep,
  'payload.step.references.sms'
> => {
  if (
    action.type === createFlowStep.type ||
    action.type === updateFlowStep.type
  ) {
    const { payload } = action as CreateFlowStep | UpdateFlowStep;
    return payload.step?.references?.sms !== undefined;
  }

  return false;
};

// match an action that updates which handoffs a flow step references
export const isStepReferencesHandoff = (
  action: UnknownAction,
): action is MakeRequired<
  CreateFlowStep | UpdateFlowStep,
  'payload.step.references.handoff'
> => {
  if (
    action.type === createFlowStep.type ||
    action.type === updateFlowStep.type
  ) {
    const { payload } = action as CreateFlowStep | UpdateFlowStep;
    return payload.step?.references?.handoff !== undefined;
  }
  return false;
};

// match an action that updates which attributes a flow step references
export const isStepReferencesAttribute = (
  action: UnknownAction,
): action is MakeRequired<
  CreateFlowStep | UpdateFlowStep,
  'payload.step.references.attribute'
> => {
  if (
    action.type === createFlowStep.type ||
    action.type === updateFlowStep.type
  ) {
    const { payload } = action as CreateFlowStep | UpdateFlowStep;
    return payload.step?.references?.attributes !== undefined;
  }
  return false;
};

// match an action that updates which attributes a no code step references
export const isNoCodeStepReferencesAttribute = (
  action: UnknownAction,
): action is MakeRequired<
  CreateNoCodeStep | UpdateNoCodeStep,
  'payload.references.attributes'
> => {
  if (
    action.type === createNoCodeStep.type ||
    action.type === updateNoCodeStep.type
  ) {
    const { payload } = action as CreateNoCodeStep | UpdateNoCodeStep;
    return payload.references?.attributes !== undefined;
  }
  return false;
};

// match an action that updates which entities a flow step references

export const isStepReferencesEntity = (
  action: UnknownAction,
): action is MakeRequired<
  CreateFlowStep | UpdateFlowStep,
  'payload.step.references.entities'
> => {
  if (
    action.type === createFlowStep.type ||
    action.type === updateFlowStep.type
  ) {
    const { payload } = action as CreateFlowStep | UpdateFlowStep;
    return payload.step?.references?.entities !== undefined;
  }
  return false;
};

// match an action that updates which entities a flow no code step references
export const isNoCodeStepReferencesEntity = (
  action: UnknownAction,
): action is MakeRequired<
  CreateNoCodeStep | UpdateNoCodeStep,
  'payload.step.references.entities'
> => {
  if (
    action.type === createNoCodeStep.type ||
    action.type === updateNoCodeStep.type
  ) {
    const { payload } = action as CreateNoCodeStep | UpdateNoCodeStep;
    return payload.references?.entities !== undefined;
  }
  return false;
};

// match an action that updates which function a flow step references
export const isStepReferencesFunction = (
  action: UnknownAction,
): action is MakeRequired<
  CreateFlowStep | UpdateFlowStep,
  'payload.step.references.globalFunctions'
> => {
  if (
    action.type === createFlowStep.type ||
    action.type === updateFlowStep.type
  ) {
    const { payload } = action as CreateFlowStep | UpdateFlowStep;
    return payload.step?.references?.globalFunctions !== undefined;
  }
  return false;
};

// match an action that deletes a flow step
export const isDeleteFlowStep = (
  action: UnknownAction,
): action is MakeRequired<DeleteFlowStep, 'payload.id'> => {
  if (action.type === deleteFlowStep.type) {
    const { payload } = action as DeleteFlowStep;
    return payload.stepId !== undefined;
  }
  return false;
};

// match an action that deletes an SMS template
export const isDeleteTemplate = (
  action: UnknownAction,
): action is MakeRequired<DeleteTemplate, 'payload.id'> => {
  if (action.type === deleteTemplate.type) {
    const { payload } = action as DeleteTemplate;
    return payload.id !== undefined;
  }

  return false;
};

// match an action that deactivates a Handoff
export const isDeleteHandoff = (
  action: UnknownAction,
): action is MakeRequired<DeleteHandoff, 'payload.id'> => {
  if (action.type === deleteHandoff.type) {
    const { payload } = action as DeleteHandoff;
    return payload.id !== undefined;
  }

  return false;
};

// match an action that deletes an attribute
export const isDeleteAttribute = (
  action: UnknownAction,
): action is MakeRequired<DeleteAttributePayload, 'payload.id'> => {
  if (action.type === deleteAttribute.type) {
    const { payload } = action as DeleteAttributePayload;
    return payload.id !== undefined;
  }

  return false;
};

export const isDeleteFunction = (
  action: UnknownAction,
): action is MakeRequired<DeleteFunction, 'payload.id'> => {
  if (action.type === deleteFunction.type) {
    const { payload } = action as DeleteFunction;
    return payload.id !== undefined;
  }

  return false;
};

export const isDeleteEntity = (
  action: UnknownAction,
): action is MakeRequired<DeleteEntity, 'payload.id'> => {
  if (action.type === deleteEntity.type) {
    const { payload } = action as DeleteEntity;
    return payload.id !== undefined;
  }

  return false;
};

// Topics matchers

export const isTopicReferencesFunction = (
  action: UnknownAction,
): action is MakeRequired<
  CreateTopic | UpdateTopic,
  'payload.references.globalFunctions'
> => {
  if (action.type === updateTopic.type || action.type === createTopic.type) {
    const { payload } = action as UpdateTopic | CreateTopic;
    return payload.references?.globalFunctions !== undefined;
  }
  return false;
};

// Stop keywords matchers
export const isStopKeywordReferencesFunction = (
  action: UnknownAction,
): action is MakeRequired<
  CreateStopKeywords | UpdateStopKeywords,
  'payload.references.globalFunctions'
> => {
  if (
    action.type === updateStopKeywords.type ||
    action.type === createStopKeywords.type
  ) {
    const { payload } = action as CreateStopKeywords | UpdateStopKeywords;
    return payload.references?.globalFunctions !== undefined;
  }
  return false;
};
